import { MaterialType } from './enums';

export type AxesType = '3-axis' | '4-axis' | '5-axis';

export interface CostModelConfig {
  axesType: AxesType;               // user-selected machine type
  removalRate: number;               // mm³/min
  machiningCostPerHour: number;      // $/hr
  setupCost: number;                 // $ per setup
}

interface MillingVector {
  xp: boolean; yp: boolean; zp: boolean;
  xm: boolean; ym: boolean; zm: boolean;
}

/** Base effective removal rates by material type for 3-axis (mm³/min).
 *  Based on mid-range industrial CNC (~12 kW), including roughing,
 *  finishing passes, tool changes, and topology-optimized geometry complexity.
 *
 *  Machine type multipliers (applied on top of base rates):
 *   - 3-axis: 1.0×  — baseline, single-direction cutting per setup
 *   - 4-axis: 1.2×  — rotary axis enables continuous cutting, fewer repositions
 *   - 5-axis: 1.5×  — optimal tool orientation, reduced cutting forces,
 *                      continuous multi-surface machining */
const BASE_REMOVAL_RATES: Record<string, number> = {
  [MaterialType.Aluminum]: 80000,
  [MaterialType.Steel]: 20000,
  [MaterialType.Titanium]: 5000,
  [MaterialType.Polymer]: 60000,
  [MaterialType.Composite]: 15000,
  [MaterialType.Nickel]: 2000,
  [MaterialType.Cobalt]: 1500,
  [MaterialType.Copper]: 40000,
  [MaterialType.Ceramic]: 1000,
};

const AXES_MRR_MULTIPLIER: Record<AxesType, number> = {
  '3-axis': 1.0,
  '4-axis': 1.2,
  '5-axis': 1.5,
};

/** Default machining cost per hour by axes type ($/hr) */
const DEFAULT_MACHINING_COST_PER_HOUR: Record<AxesType, number> = {
  '3-axis': 75,
  '4-axis': 100,
  '5-axis': 150,
};

/** Default setup cost per setup by axes type ($) */
const DEFAULT_SETUP_COST: Record<AxesType, number> = {
  '3-axis': 50,
  '4-axis': 75,
  '5-axis': 100,
};

/**
 * Count selected directions per axis.
 */
function getDirectionsPerAxis(mv: MillingVector): { x: number; y: number; z: number } {
  return {
    x: (mv.xp ? 1 : 0) + (mv.xm ? 1 : 0),
    y: (mv.yp ? 1 : 0) + (mv.ym ? 1 : 0),
    z: (mv.zp ? 1 : 0) + (mv.zm ? 1 : 0),
  };
}

/**
 * Total number of selected milling directions.
 */
function countDirections(mv: MillingVector): number {
  const d = getDirectionsPerAxis(mv);
  return d.x + d.y + d.z;
}

/**
 * Suggest an axes type based on milling direction count.
 * Used as the initial default when the user first enables Milling2.
 * 1 unique axis → 3-axis, 2 → 4-axis, 3 → 5-axis
 */
export function suggestAxesType(millingVector: MillingVector): AxesType {
  const d = getDirectionsPerAxis(millingVector);
  const uniqueCount = (d.x > 0 ? 1 : 0) + (d.y > 0 ? 1 : 0) + (d.z > 0 ? 1 : 0);

  if (uniqueCount <= 1) return '3-axis';
  if (uniqueCount === 2) return '4-axis';
  return '5-axis';
}

/**
 * Conservative 4-axis setup calculation.
 *
 * The rotary axis is restricted to one of the selected axes
 * (conservative — assumes the machine's rotary axis may not be
 * the optimal unselected third axis).
 *
 * For each candidate rotary axis R among the selected axes:
 *   - Directions along R = axial (1 physical flip each)
 *   - Directions perpendicular to R = radial (1 setup covers all)
 *   - S = (any radial ? 1 : 0) + axial count
 *
 * Returns the minimum S across candidates.
 */
function calculate4AxisSetups(mv: MillingVector): number {
  const d = getDirectionsPerAxis(mv);
  const axes = (['x', 'y', 'z'] as const).filter(a => d[a] > 0);

  if (axes.length === 0) return 0;

  let minSetups = Infinity;
  for (const rotary of axes) {
    const axialCount = d[rotary];
    const radialCount = axes.filter(a => a !== rotary).reduce((sum, a) => sum + d[a], 0);
    const setups = (radialCount > 0 ? 1 : 0) + axialCount;
    minSetups = Math.min(minSetups, setups);
  }

  return minSetups;
}

/**
 * Calculate number of setups based on user-selected axes type.
 *
 * 3-axis: S = N — every direction requires a physical repositioning.
 * 4-axis: Conservative model — rotary axis groups some directions,
 *         axial directions (ends/faces) need physical flips.
 * 5-axis: S = min(N, 2) — primary machining + finishing/flip.
 */
export function getNumberOfSetups(millingVector: MillingVector, axesType: AxesType): number {
  const n = countDirections(millingVector);
  if (n === 0) return 0;

  switch (axesType) {
    case '3-axis':
      return n;
    case '4-axis':
      return calculate4AxisSetups(millingVector);
    case '5-axis':
      return Math.min(n, 2);
  }
}

/**
 * Get default machine parameters for an axes type (hourly rate + setup cost).
 */
export function getDefaultMachineParams(axesType: AxesType): { machiningCostPerHour: number; setupCost: number } {
  return {
    machiningCostPerHour: DEFAULT_MACHINING_COST_PER_HOUR[axesType],
    setupCost: DEFAULT_SETUP_COST[axesType],
  };
}

/**
 * Get default removal rate for a material + machine type (mm³/min).
 */
export function getDefaultRemovalRate(materialType: string, axesType: AxesType = '3-axis'): number {
  const base = BASE_REMOVAL_RATES[materialType] ?? 10000;
  return Math.round(base * AXES_MRR_MULTIPLIER[axesType]);
}

/**
 * Build default CostModelConfig from material type and axes type.
 */
export function getDefaultCostModel(materialType: string, axesType: AxesType): CostModelConfig {
  return {
    axesType,
    removalRate: getDefaultRemovalRate(materialType, axesType),
    machiningCostPerHour: DEFAULT_MACHINING_COST_PER_HOUR[axesType],
    setupCost: DEFAULT_SETUP_COST[axesType],
  };
}
